import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { BandReveal, HeroReveal, InteractiveDots, Pressable, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import { DocThumb, type DocKind } from '@/components/landing/service'
import type { IndustryData, Template } from '@/data/industries'
import { DrawingRegister } from './DrawingRegister'
import { IndustryCtaBand } from './IndustryCtaBand'
import { APP_ICON, joinList, templateExcerpt } from './listing'
import { WorkflowRows } from './WorkflowRows'

const DOC_KIND: Record<Template['type'], DocKind> = { Word: 'doc', Excel: 'sheet', PowerPoint: 'slide', PDF: 'doc' }
const DOC_ACCENT = ['blue', 'pink', 'purple', 'green'] as const

/** "67% faster" → ["67%", "faster"]. */
function splitImprovement(text: string): [string, string] {
  const i = text.indexOf(' ')
  return i === -1 ? [text, ''] : [text.slice(0, i), text.slice(i + 1)]
}

/**
 * Giant-word layout (Engineering): the industry's one word at display size
 * across the page, a document register directly under it, then H1, lede and
 * CTA in one row. The workflows as large hover rows, the ROI metrics as a
 * pink numbers band, the templates as a register-style grid and a green
 * CTA band.
 */
export function GiantWordLayout({ industry }: { industry: IndustryData }) {
  const word = industry.shortName

  return (
    <>
      {/* 1. Hero: giant word, register fragment, then H1 + lede + CTA in a row */}
      <section className="overflow-x-clip pb-[clamp(3rem,8vh,6rem)] pt-[clamp(2.5rem,7vh,5rem)]">
        <div className="landing-container">
          <HeroReveal delay={0} distance={40}>
            <p
              aria-hidden
              className="whitespace-nowrap font-[family-name:var(--font-landing-display)] text-[calc((100vw-3rem)/8.1)] uppercase leading-[1.15] tracking-[-0.04em] text-[var(--color-landing-gray-900)] dark:text-white 2xl:text-[min(calc((100vw-4rem)/8.1),18rem)]"
            >
              {word}
              <span className="text-[#ff00f2]">.</span>
            </p>
          </HeroReveal>
          <HeroReveal delay={0.15} distance={30}>
            <DrawingRegister industry={industry} className="mt-4 md:mt-6" />
          </HeroReveal>

          <div className="mt-12 grid gap-8 md:mt-16 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_auto] lg:items-end lg:gap-14">
            <HeroReveal delay={0.25}>
              <h1 className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">{industry.headline}</h1>
            </HeroReveal>
            <HeroReveal delay={0.32}>
              <p className="landing-text-body-lg max-w-[46ch] border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                {industry.subheadline}
              </p>
            </HeroReveal>
            <HeroReveal delay={0.4} className="flex flex-wrap gap-3 lg:flex-col lg:items-stretch">
              <Pressable className="inline-block">
                <Link to="/contact" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep w-full">
                  Book a demo
                  <ArrowRight aria-hidden className="h-5 w-5" />
                </Link>
              </Pressable>
              <Link to="/register" className="landing-cta landing-cta--outline landing-cta--lg landing-btn-sweep">
                Start free trial
              </Link>
            </HeroReveal>
          </div>
        </div>
      </section>

      {/* 2. Workflows: large hover rows */}
      <section aria-labelledby="workflows-heading" className="landing-section !pt-8 md:!pt-12">
        <div className="landing-container mb-10 md:mb-14">
          <TextReveal as="h2" className="landing-text-display-xl max-w-[20ch] text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="workflows-heading">
              From RFI to <span className="landing-highlight">spec set.</span>
            </span>
          </TextReveal>
        </div>
        <WorkflowRows industry={industry} />
      </section>

      {/* 3. ROI: pink numbers band */}
      <BandReveal>
        <section aria-labelledby="roi-heading" className="landing-section-lg relative overflow-hidden bg-[#ff00f2]">
          <InteractiveDots />
          <div className="landing-container relative z-10">
            <h2 id="roi-heading" className="landing-text-display-lg max-w-[24ch] text-white">
              Measured on the work itself
            </h2>
            <StaggerContainer className="mt-14 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4 md:mt-20">
              {industry.roiMetrics.map((metric) => {
                const [figure, word] = splitImprovement(metric.improvement)
                return (
                  <StaggerItem key={metric.metric} className="min-w-0 border-t-2 border-white/80 pt-6">
                    <p className="whitespace-nowrap font-[family-name:var(--font-landing-display)] text-[clamp(3rem,6.2vw,6.5rem)] uppercase leading-[1.15] tracking-[-0.03em] text-white tabular-nums">
                      {figure}
                    </p>
                    <h3 className="mt-3 text-lg font-bold uppercase leading-snug tracking-[0.01em] text-white md:text-xl">
                      {metric.metric}
                    </h3>
                    <p className="mt-2 text-base font-semibold text-[#0a0a0a]">
                      <span className="capitalize">{word}</span>: {metric.before}{' '}
                      <ArrowRight aria-hidden className="inline h-4 w-4" />
                      <span className="sr-only"> to </span> {metric.after}
                    </p>
                  </StaggerItem>
                )
              })}
            </StaggerContainer>
          </div>
        </section>
      </BandReveal>

      {/* 4. Templates: register-style grid */}
      <section id="templates" aria-labelledby="templates-heading" className="landing-section scroll-mt-20">
        <div className="landing-container">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-end lg:gap-20">
            <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
              <span id="templates-heading">
                The <span className="landing-highlight landing-highlight--secondary">drawing set.</span>
              </span>
            </TextReveal>
            <ScrollReveal direction="none" delay={0.15}>
              <p className="landing-text-body-lg max-w-[48ch] border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                {industry.templates.length} templates for the documents a project lives on, which answer to {joinList(industry.complianceRequirements.map((r) => (/^[A-Z]{2}/.test(r) ? r : r.toLowerCase())))}.
              </p>
            </ScrollReveal>
          </div>

          <ScrollReveal direction="none" amount={0.1}>
            <ul className="mt-14 grid gap-px border border-[var(--color-landing-gray-900)] bg-[var(--color-landing-gray-900)] sm:grid-cols-2 lg:grid-cols-4 dark:border-white/60 dark:bg-white/60">
              {industry.templates.map((template, i) => {
                const app = APP_ICON[template.type]
                return (
                  <li key={template.name} className="bg-white dark:bg-[#0a0a0f]">
                    <Link
                      to="/platform/templates"
                      className="group flex h-full flex-col gap-5 p-5 transition-colors duration-200 hover:bg-[var(--color-landing-gray-50)] dark:hover:bg-[#15151d] md:p-6"
                    >
                      <span className="flex items-center justify-between font-[family-name:var(--font-landing-mono)] text-xs text-[var(--color-landing-gray-500)]">
                        {String(i + 1).padStart(2, '0')}
                        {app && <OfficeAppIcon app={app} className="h-5 w-5" />}
                      </span>
                      <span className="mx-auto block w-[46%] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-1">
                        <DocThumb kind={DOC_KIND[template.type]} title={template.name} accent={DOC_ACCENT[i % DOC_ACCENT.length]} className="aspect-[3/4] w-full" />
                      </span>
                      <span className="mt-auto">
                        <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-base font-bold uppercase leading-tight tracking-[0.01em] text-[var(--color-landing-gray-900)] dark:text-white">
                          {template.name}
                        </h3>
                        <p className="mt-1.5 line-clamp-2 text-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                          {templateExcerpt(template)}
                        </p>
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          </ScrollReveal>
        </div>
      </section>

      {/* 5. CTA band: green */}
      <IndustryCtaBand industry={industry} tone="green" />
    </>
  )
}
